import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'what-to-do-if-an-organisation-misses-the-deadline';

export const DeadlineMissed = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        An organisation has one calendar month to respond to a Subject Access Request, extendable
        by up to two further months for a genuinely complex request — three months at the
        absolute most, and only if they told you about the extension within the first month.
        Once that deadline has passed with no records and no explanation, they are in breach of
        the law. Here is exactly what to do next, in order.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Step 1" title="Check the clock, not just the calendar">
      <GuideChecklist
        items={[
          "The month is calculated from the day the organisation received a valid request — not from when you sent it, and not from when they first replied to acknowledge it, if that reply came later.",
          "If they asked you for identity documents, the clock often doesn't start (or pauses) until you've provided what they reasonably needed — check whether that's genuinely what happened, rather than the request having simply gone quiet.",
          'If they told you, within the first month, that they were extending because the request is complex, their real deadline is up to two months later — check whether you\'re actually past that extended date, not just the original one.',
        ]}
      />
    </GuideSection>

    <GuideSection eyebrow="Step 2" title="Chase them in writing">
      <p>
        Send a short, clear follow-up by email or letter — whatever the request was originally
        made by, since the response has to come back the same way it was made when that was
        electronic. State the date of your original request, note that the statutory deadline
        has now passed, and ask for a date by which you&apos;ll receive a full response.
      </p>
      <GuideNote>
        <p>
          <strong>Keep it factual, not confrontational.</strong> A short, dated chase-up is more
          effective, and easier to point to later if you need to escalate, than a long or
          frustrated message. You are not asking for a favour — you are asking them to meet a
          legal deadline they have already missed.
        </p>
      </GuideNote>
    </GuideSection>

    <GuideCTA />

    <GuideSection eyebrow="Step 3" title="Complain to the ICO">
      <p>
        If a chase-up gets no response, or an unsatisfactory one, you can complain to the{' '}
        <strong className="text-ink">Information Commissioner&apos;s Office (ICO)</strong>, the UK&apos;s
        independent regulator for data protection. The ICO can investigate, and has powers to
        require an organisation to comply. Complaining is free and doesn&apos;t require a solicitor.
      </p>
      <GuideChecklist
        items={[
          "Have your original request (and its date), any acknowledgement you received, and any chase-up you sent, ready to hand — the more specific your timeline, the easier the ICO's job.",
          <>
            Complaints can be started at{' '}
            <SourceLink href="https://ico.org.uk/make-a-complaint/">
              ico.org.uk/make-a-complaint
            </SourceLink>
            .
          </>,
          "The ICO generally expects you to have raised the issue with the organisation first, which is exactly what step 2 above does — so don't skip it.",
        ]}
      />
    </GuideSection>

    <GuideSection eyebrow="What the ICO can and can't do" title="Setting expectations">
      <p>
        The ICO can investigate, issue guidance to the organisation, and in serious or repeated
        cases take formal enforcement action. What it generally won&apos;t do is act as your
        personal case worker chasing a single organisation on your behalf day to day — it deals
        in patterns and formal complaints, not real-time nagging. For that kind of ongoing
        tracking and chasing, a tool built for exactly this — or simply your own calendar
        reminders — does more of the daily work.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="deadline-missed-faq">
        <GuideFaqItem
          q="Can they ask me clarifying questions to buy more time?"
          a="They can ask for clarification about what you're requesting, which can pause the clock while they wait for your answer, under the Data (Use and Access) Act 2025's changes to Article 12A. This has to be a genuine, specific question about scope though — it can't be used as an indefinite delay tactic, or asked for information they already have."
        />
        <GuideFaqItem
          q="What if they say they need more time because my request is complex?"
          a="That's allowed, up to two further months beyond the first, but only if they told you within the first month and explained why. An extension announced after the original deadline has already passed doesn't count — that's simply a missed deadline with an excuse attached."
        />
        <GuideFaqItem
          q="Is there a time limit on complaining to the ICO?"
          a="The ICO doesn't set a strict cut-off, but it's best to raise a complaint reasonably promptly once you believe there's been a breach — the fresher your timeline and evidence, the easier it is for them to act on it."
        />
        <GuideFaqItem
          q="Will complaining to the ICO make the organisation angry or affect anything else I need from them?"
          a="A SAR complaint is about a legal compliance failure, not a personal dispute — it's a routine, structured process the ICO handles constantly, not confrontation. It shouldn't affect any other services or support you're entitled to."
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        The ICO explains timescales and what counts as a valid extension in its{' '}
        <SourceLink href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/individual-rights/right-of-access/">
          right of access guidance
        </SourceLink>
        . For the basics of what a SAR is and how deadlines work, see our{' '}
        <Link to="/guides/what-is-a-subject-access-request" className="text-brand underline hover:opacity-90">
          plain-English guide
        </Link>
        .
      </p>
    </GuideSection>

    <GuideCTA
      heading="We track every deadline automatically"
      body="Precious Data tracks the legal deadline for every request you send, chases anyone who goes quiet, and flags exactly when you're entitled to complain to the ICO — so you don't have to watch the calendar yourself."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default DeadlineMissed;
